(() => {
  if (window.upperdeckysNativeOptionsScriptLoaded) return;
  window.upperdeckysNativeOptionsScriptLoaded = true;

  const SELECTOR = '[data-ud-native-option-root]';

  const formatMoney = (amount, currency) => {
    if (typeof amount !== 'number') return '';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency,
        minimumFractionDigits: amount % 100 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(amount / 100);
    } catch (_error) {
      return `$${(amount / 100).toFixed(2).replace(/\.00$/, '')}`;
    }
  };

  const init = () => {
    const roots = [...document.querySelectorAll(SELECTOR)];
    if (!roots.length) return;

    const variantsNode = roots[0].querySelector('[data-ud-native-variants]');
    const productNode = roots[0].querySelector('[data-ud-native-product]');
    if (!variantsNode || !productNode) return;

    let variants;
    try {
      variants = JSON.parse(variantsNode.textContent);
    } catch (_error) {
      return;
    }
    if (!Array.isArray(variants) || !variants.length) return;

    window.upperdeckysNativeOptionsController?.abort();
    const controller = new AbortController();
    window.upperdeckysNativeOptionsController = controller;
    const { signal } = controller;
    const currency = productNode.dataset.currency || 'USD';
    // Keep these controls outside Moonbundle's generic theme-picker selectors.
    // The bundle app scans `options[...]` names and `data-option-position`, then
    // clicks the first configured variant after load. Our variant ID and the
    // bundle item selectors are synced explicitly below instead.
    const inputs = roots.flatMap((root) => [...root.querySelectorAll('input[type="radio"][data-ud-option-position]')]);
    const flavorInputs = roots
      .filter((root) => root.dataset.optionRole === 'flavor')
      .flatMap((root) => [...root.querySelectorAll('input[type="radio"][data-ud-option-position]')]);

    const selectedValues = () => {
      const values = [];
      inputs.forEach((input) => {
        if (input.checked) values[Number(input.dataset.udOptionPosition) - 1] = input.value;
      });
      return values;
    };

    const chooseVariant = (changedInput) => {
      const selected = selectedValues();
      const exact = variants.find((variant) => variant.options.every((value, index) => selected[index] === value));
      if (exact) return exact;

      const changedIndex = Number(changedInput.dataset.udOptionPosition) - 1;
      const candidates = variants.filter((variant) => variant.options[changedIndex] === changedInput.value);
      if (!candidates.length) return null;

      return candidates.sort((left, right) => {
        const leftMatches = left.options.reduce((count, value, index) => count + (selected[index] === value ? 1 : 0), 0);
        const rightMatches = right.options.reduce((count, value, index) => count + (selected[index] === value ? 1 : 0), 0);
        return rightMatches - leftMatches;
      })[0];
    };

    const syncInputs = (variant) => {
      inputs.forEach((input) => {
        const optionIndex = Number(input.dataset.udOptionPosition) - 1;
        const selected = variant.options[optionIndex] === input.value;
        input.checked = selected;
        const label = input.nextElementSibling;
        if (!label) return;
        label.setAttribute('aria-current', selected ? (label.classList.contains('ud-pdp__format') ? 'page' : 'true') : 'false');
      });
    };

    const syncFlavorChoices = (variant) => {
      for (const input of flavorInputs) {
        const flavorIndex = Number(input.dataset.udOptionPosition) - 1;
        const availableForFormat = variants.some((candidate) => (
          candidate.options[flavorIndex] === input.value
          && candidate.options.every((value, index) => index === flavorIndex || value === variant.options[index])
        ));
        const choice = input.closest('[data-ud-dependent-option]');
        input.disabled = !availableForFormat;
        if (!availableForFormat) input.checked = false;
        if (choice) choice.hidden = !availableForFormat;
      }
    };

    const syncForms = (variant) => {
      document.querySelectorAll('[data-ud-parent-variant-input]').forEach((input) => {
        input.value = String(variant.id);
        input.disabled = !variant.available;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      document.querySelectorAll('[data-ud-parent-submit]').forEach((button) => {
        const owner = button.closest('[data-ud-parent-purchase]');
        const text = button.querySelector('span');
        const availableLabel = owner?.dataset.availableLabel || owner?.dataset.atcLabel || 'Add To Cart';
        const soldOutLabel = owner?.dataset.soldOutLabel || 'Sold Out';
        button.disabled = !variant.available;
        button.toggleAttribute('aria-disabled', !variant.available);
        if (text) text.textContent = variant.available ? availableLabel : soldOutLabel;
      });

      const purchase = document.querySelector('[data-ud-pdp-module="purchase"]');
      if (purchase) {
        purchase.dataset.productAvailable = variant.available ? 'true' : 'false';
        purchase.dataset.oneTimePrice = formatMoney(variant.price, currency);
        if (variant.available) delete purchase.dataset.upperdeckysUnavailable;
      }
    };

    const syncPrice = (variant) => {
      document.querySelectorAll('[data-ref="price"]').forEach((node) => {
        node.textContent = formatMoney(variant.price, currency);
      });
      document.querySelectorAll('[data-ref="compare-at-price"]').forEach((node) => {
        node.textContent = variant.compare_at_price > variant.price
          ? formatMoney(variant.compare_at_price, currency)
          : '';
      });
    };

    const syncUrl = (variant) => {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    };

    let moonbundleSyncToken = 0;
    const syncMoonbundleItems = async (variant) => {
      const syncToken = ++moonbundleSyncToken;
      const rows = [...document.querySelectorAll('.moonbundle-box .moonbundle-variant-row')];
      if (!rows.length) return false;

      for (const row of rows) {
        const selects = [...row.querySelectorAll('.moonbundle-option-selector select')];
        for (let optionIndex = 0; optionIndex < variant.options.length; optionIndex += 1) {
          if (syncToken !== moonbundleSyncToken) return false;
          const select = selects[optionIndex];
          const value = variant.options[optionIndex];
          if (!select || value == null || ![...select.options].some((option) => option.value === value)) continue;
          if (select.value !== value) {
            select.value = value;
            select.dispatchEvent(new Event('input', { bubbles: true }));
            select.dispatchEvent(new Event('change', { bubbles: true }));
            await new Promise((resolve) => window.setTimeout(resolve, 0));
          }
        }
      }

      document.querySelectorAll('.moonbundle-box').forEach((box) => {
        box.dataset.upperdeckysNativeVariantId = String(variant.id);
        box.classList.add('is-native-variant-synced');
      });
      return true;
    };

    const scheduleMoonbundleSync = (variant, remainingAttempts = 40) => {
      const run = async () => {
        if (signal.aborted) return;
        const synced = await syncMoonbundleItems(variant);
        if (!synced && remainingAttempts > 1) {
          window.setTimeout(() => scheduleMoonbundleSync(variant, remainingAttempts - 1), 250);
        }
      };
      run();
    };

    const applyVariant = (variant, updateUrl = true) => {
      if (!variant) return;
      syncInputs(variant);
      syncFlavorChoices(variant);
      syncForms(variant);
      syncPrice(variant);
      if (updateUrl) syncUrl(variant);
      scheduleMoonbundleSync(variant);
      document.dispatchEvent(new CustomEvent('upperdeckys:variant-change', {
        detail: { variant },
      }));
    };

    inputs.forEach((input) => {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        applyVariant(chooseVariant(input));
      }, { signal });
    });

    const requestedId = Number(new URL(window.location.href).searchParams.get('variant'));
    const initialId = requestedId || Number(productNode.dataset.selectedVariantId);
    applyVariant(variants.find((variant) => variant.id === initialId) || variants[0], false);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', () => {
    init();
  });
})();
